import React, { useRef, useCallback, useMemo, useEffect } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import { ThumbsUp, ThumbsDown } from "lucide-react-native";
import { Text } from "@src/packages/medical-history/components/MedicalHistoryText";
import {
  QuestionLayout,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useSexualHealth } from "../SexualHealthContext";
import { buildSexualHealthNavigationStack } from "../buildSexualHealthNavigationStack";
import type { SexualHealthStackParamList } from "../SexualHealthNavigator";
import { useMedicalHistoryTheme } from "../../../theme/MedicalHistoryTheme";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

const MIN = 1;
const MAX = 10;
const STEPS = MAX - MIN;
const THUMB_SIZE = 32;

export function PainfulIntercourseScreen() {
  const t = useTranslations();
  const capturePress = usePressCapture("PainfulIntercourseScreen");
  const { state, updateState, save } = useSexualHealth();
  const navigation =
    useNavigation<NavigationProp<SexualHealthStackParamList>>();
  const trackRef = useRef<View>(null);
  const theme = useMedicalHistoryTheme();

  const selection = useMemo(() => {
    if (state.hasPainfulIntercourse == null) return null;
    return state.hasPainfulIntercourse ? "yes" : "no";
  }, [state.hasPainfulIntercourse]);

  const painValue = state.painfulIntercourseScale;

  // Default pain to 1 when user says yes
  useEffect(() => {
    if (
      state.hasPainfulIntercourse === true &&
      state.painfulIntercourseScale === null
    ) {
      updateState({ painfulIntercourseScale: 1 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.hasPainfulIntercourse]);

  const handleChange = (value: "yes" | "no") => {
    if (value === "no") {
      updateState({
        hasPainfulIntercourse: false,
        painfulIntercourseScale: null,
      });
    } else {
      updateState({ hasPainfulIntercourse: true });
    }
  };

  const valueFromEvent = useCallback((pageX: number) => {
    return new Promise<number>((resolve) => {
      const el = trackRef.current;
      if (!el) return;
      el.measureInWindow((x: number, _y: number, width: number) => {
        const relative = pageX - x;
        const clamped = Math.max(0, Math.min(relative, width));
        const raw = MIN + (clamped / width) * STEPS;
        resolve(Math.round(raw));
      });
    });
  }, []);

  const handlePress = useCallback(
    async (pageX: number) => {
      const v = await valueFromEvent(pageX);
      updateState({ painfulIntercourseScale: v });
    },
    [valueFromEvent, updateState],
  );

  const handleSave = async () => {
    await save();
    const stack = buildSexualHealthNavigationStack(state);
    const currentIdx = stack.indexOf("PainfulIntercourse");
    const nextRoute = stack[currentIdx + 1];
    if (nextRoute) {
      navigation.navigate(nextRoute);
    }
  };

  const fraction = painValue != null ? (painValue - MIN) / STEPS : 0;

  return (
    <QuestionLayout
      title={t("med_history.gynae.sexual_health.painful_intercourse_title")}
      subtitle={t(
        "med_history.gynae.sexual_health.painful_intercourse_subtitle",
      )}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={selection === null}
      saveLabel={t("med_history.gynae.sexual_health.continue")}
    >
      <View style={styles.container}>
        <View style={styles.yesNoRow}>
          <SquareOptionButton
            icon={ThumbsUp}
            label={t("med_history.shared.yes")}
            isSelected={selection === "yes"}
            onPress={() => handleChange("yes")}
            iconSize={24}
            containerSize={48}
            labelSize="lg"
          />
          <SquareOptionButton
            icon={ThumbsDown}
            label={t("med_history.shared.no")}
            isSelected={selection === "no"}
            onPress={() => handleChange("no")}
            iconSize={24}
            containerSize={48}
            labelSize="lg"
          />
        </View>

        {selection === "yes" && (
          <View style={styles.sliderSection}>
            <Text
              style={{
                color: theme.textMuted,
                fontSize: 14,
                fontWeight: "500",
                letterSpacing: 0.5,
                marginBottom: 12,
                paddingLeft: 4,
              }}
            >
              {t(
                "med_history.gynae.sexual_health.painful_intercourse_rate_label",
              )}
            </Text>

            <View style={styles.valueDisplay}>
              <Text style={[styles.valueText, { color: theme.accentLight }]}>
                {painValue ?? "–"}
              </Text>
            </View>

            <View style={styles.sliderWrapper}>
              <View
                ref={trackRef}
                style={styles.track}
                onStartShouldSetResponder={() => true}
                onMoveShouldSetResponder={() => true}
                onResponderGrant={(e) => handlePress(e.nativeEvent.pageX)}
                onResponderMove={(e) => handlePress(e.nativeEvent.pageX)}
              >
                <View
                  style={[
                    styles.trackBackground,
                    { backgroundColor: theme.inputBorder },
                  ]}
                />
                <View
                  style={[
                    styles.trackFilled,
                    {
                      width: painValue != null ? `${fraction * 100}%` : "0%",
                      backgroundColor: theme.accentLight,
                    },
                  ]}
                />
                {painValue != null && (
                  <View
                    style={[
                      styles.thumb,
                      {
                        left: `${fraction * 100}%`,
                        marginLeft: -THUMB_SIZE / 2,
                        backgroundColor: theme.accentLight,
                        shadowColor: theme.accentLight,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.thumbText,
                        { color: theme.completeButtonBg },
                      ]}
                    >
                      {painValue}
                    </Text>
                  </View>
                )}
              </View>
            </View>

            <View style={styles.tickTapRow}>
              {Array.from({ length: 10 }, (_, i) => i + 1).map((num) => (
                <Pressable
                  key={num}
                  style={styles.tickTap}
                  onPress={() => {
                    capturePress(
                      pressLabel(String(num)),
                      "painful_intercourse_scale",
                    );
                    updateState({ painfulIntercourseScale: num });
                  }}
                >
                  <Text
                    style={[
                      styles.tickLabel,
                      {
                        color:
                          painValue === num
                            ? theme.accentLight
                            : theme.textFaint,
                      },
                      ...(painValue === num ? [styles.tickLabelActive] : []),
                    ]}
                  >
                    {num}
                  </Text>
                </Pressable>
              ))}
            </View>

            <View style={styles.labelRow}>
              <Text style={{ fontSize: 12, color: theme.textFaint }}>
                {t("med_history.gynae.sexual_health.no_pain")}
              </Text>
              <Text style={{ fontSize: 12, color: theme.textFaint }}>
                {t("med_history.gynae.sexual_health.worst_pain")}
              </Text>
            </View>
          </View>
        )}
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
  },
  yesNoRow: {
    flexDirection: "row",
    gap: 12,
    width: "100%",
    marginBottom: 24,
  },
  sliderSection: {
    width: "100%",
  },
  valueDisplay: {
    alignItems: "center",
    marginBottom: 24,
  },
  valueText: {
    fontSize: 48,
    fontWeight: "700",
  },
  sliderWrapper: {
    paddingHorizontal: THUMB_SIZE / 2,
    marginBottom: 8,
  },
  track: {
    height: 44,
    justifyContent: "center",
    position: "relative",
    cursor: "pointer",
  },
  trackBackground: {
    position: "absolute",
    left: 0,
    right: 0,
    height: 6,
    borderRadius: 3,
  },
  trackFilled: {
    position: "absolute",
    left: 0,
    height: 6,
    borderRadius: 3,
  },
  thumb: {
    position: "absolute",
    width: THUMB_SIZE,
    height: THUMB_SIZE,
    borderRadius: THUMB_SIZE / 2,
    alignItems: "center",
    justifyContent: "center",
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.4,
    shadowRadius: 8,
    elevation: 4,
  },
  thumbText: {
    fontSize: 13,
    fontWeight: "700",
  },
  tickTapRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  tickTap: {
    flex: 1,
    alignItems: "center",
    paddingVertical: 4,
  },
  tickLabel: {
    fontSize: 11,
    fontWeight: "500",
    textAlign: "center",
  },
  tickLabelActive: {
    fontWeight: "700",
  },
  labelRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: 4,
  },
});
